import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import Component from '@glimmer/component';
import { cached } from '@glimmer/tracking';
import { PrismSelectInput } from '@mylighthouse/prism-ember';
import type { Option } from '@mylighthouse/prism-ember/components/prism/select-input';
import dayjs from 'dayjs';
import { t } from 'ember-intl';
import { openUrl } from 'frontend-utils/utils/url/open-url';
import GeneralSmartInsightsDetailViewSmartInsightCard from 'lumen/components/general/smart-insights/detail-view/smart-insight-card';
import GeneralSmartInsightsDetailViewSmartInsightKpiBlock from 'lumen/components/general/smart-insights/detail-view/smart-insight-kpi-block';
import type { SmartInsightDay } from 'lumen/models';
import type { SmartInsightDayInsightEntrySource } from 'lumen/models/smart-insights/smart-insight-day-insight-entry';
import type { MarketDemandLevel } from 'lumen/utils/constants/general-constants';
import { RATE_PARAMS } from 'lumen/utils/constants/general-constants';
import {
  ACTIVE_FILTERS,
  DETAIL_PANE_DETAIL_SECTION_CARD_IDS,
} from 'lumen/utils/constants/market-insight-constants';
import { OD_SELECTED_TILES_IDS } from 'lumen/utils/constants/overview-dashboard-constants';
import { RATES_VIEWS } from 'lumen/utils/constants/rate-constants';
import {
  SMART_INSIGHTS_KPI_COMPARISON_PERIOD_TRANSLATIONS,
  SMART_INSIGHTS_KPI_COMPARISON_PERIODS,
  type SmartInsightsKpiComparisonPeriod,
} from 'lumen/utils/constants/smart-insights';
import { getDemandLevel } from 'lumen/utils/demand-level';
import { stringifyObject } from 'lumen/utils/parse-utils';
import { DAY_BY_DAY_PATHS } from 'lumen/utils/spider';

import styles from './smart-insight-kpi-blocks.module.css';

interface GeneralSmartInsightsDetailViewSmartInsightKpiBlocksSignature {
  Args: {
    setSmartInsightsKpiComparisonPeriod: (
      period: SmartInsightsKpiComparisonPeriod,
    ) => void;
    smartInsightDay: SmartInsightDay;
    smartInsightsKpiComparisonPeriod: SmartInsightsKpiComparisonPeriod;
  };
}

type ComparisonPeriodOption = Option & {
  label: string;
  value: SmartInsightsKpiComparisonPeriod;
};

export default class GeneralSmartInsightsDetailViewSmartInsightKpiBlocks extends Component<GeneralSmartInsightsDetailViewSmartInsightKpiBlocksSignature> {
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];
  @service declare intl: Services['intl'];
  @service declare router: Services['router'];

  get adrChangeValue(): number | undefined {
    switch (this.args.smartInsightsKpiComparisonPeriod) {
      case SMART_INSIGHTS_KPI_COMPARISON_PERIODS.YESTERDAY:
        return this.kpis.adrPickupVsYesterday;
      default:
        return undefined;
    }
  }

  get adrChangeValueFormatted(): string | undefined {
    const changeValue = this.adrChangeValue;

    if (changeValue === undefined) {
      return undefined;
    }

    const signDisplay = changeValue === 0 ? 'never' : 'always';

    return this.intl.formatNumber(changeValue, {
      style: 'currency',
      currency: this.currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
      signDisplay,
    });
  }

  get adrValueFormatted(): string {
    return this.intl.formatNumber(this.kpis.adr, {
      style: 'currency',
      currency: this.currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
  }

  private get biRedirectId(): string {
    return this.args.smartInsightDay.meta?.biRedirectId ?? this.subscriptionId;
  }

  @cached
  get comparisonPeriodOptions(): ComparisonPeriodOption[] {
    return Object.values(SMART_INSIGHTS_KPI_COMPARISON_PERIODS).map(
      (period) => ({
        label: this.intl.t(
          SMART_INSIGHTS_KPI_COMPARISON_PERIOD_TRANSLATIONS[period],
        ),
        value: period,
      }),
    );
  }

  get currency(): string | undefined {
    const { hotel, market } = this.hotelAndMarketService;

    if (market) {
      return market.revenueInsightCurrency ?? market.currencyCode;
    }

    return hotel?.revenueInsightCurrency ?? hotel?.currencyCode;
  }

  get demandLevel(): MarketDemandLevel | null {
    return getDemandLevel(this.kpis.demand);
  }

  get demandLinkText(): string {
    return this.intl.t('smart_insights.detail_view.kpi_source_link', {
      productName: this.demandSectionSource?.productName ?? '',
    });
  }

  @cached
  get demandSectionSource(): SmartInsightDayInsightEntrySource | null {
    const sources = [
      this.buildMarketInsightSource(),
      this.buildRateInsightSource(),
      this.buildOverviewSource(OD_SELECTED_TILES_IDS.demand_level),
    ];

    return sources.find((s) => s.hasAccess) ?? null;
  }

  get demandValueFormatted(): string {
    return this.intl.formatNumber(this.kpis.demand, {
      style: 'percent',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
      signDisplay: 'never',
    });
  }

  get isAdrBlockVisible(): boolean {
    return this.kpis.adr !== null && this.kpis.adr !== undefined;
  }

  get isComparisonPeriodDropdownVisible(): boolean {
    return (
      !isEmpty(this.kpis.occupancyPickupVsYesterday) ||
      !isEmpty(this.kpis.roomNightsPickupVsYesterday) ||
      !isEmpty(this.kpis.adrPickupVsYesterday)
    );
  }

  get isDividerVisible(): boolean {
    return this.isMarketDemandBlockVisible && this.isOtbSectionVisible;
  }

  get isMarketDemandBlockVisible(): boolean {
    return this.kpis.demand !== null && this.kpis.demand !== undefined;
  }

  get isOccupancyBlockVisible(): boolean {
    return this.kpis.occupancy !== null && this.kpis.occupancy !== undefined;
  }

  get isOtbSectionVisible(): boolean {
    return (
      this.isOccupancyBlockVisible ||
      this.isRoomNightsBlockVisible ||
      this.isAdrBlockVisible
    );
  }

  get isRoomNightsBlockVisible(): boolean {
    return this.kpis.roomNights !== null && this.kpis.roomNights !== undefined;
  }

  private get kpis(): SmartInsightDay['kpis'] {
    return this.args.smartInsightDay.kpis;
  }

  get occupancyChangeValue(): number {
    switch (this.args.smartInsightsKpiComparisonPeriod) {
      case SMART_INSIGHTS_KPI_COMPARISON_PERIODS.THREE_DAYS_AGO:
        return this.kpis.occupancyPickupVs3DaysAgo;
      case SMART_INSIGHTS_KPI_COMPARISON_PERIODS.SEVEN_DAYS_AGO:
        return this.kpis.occupancyPickupVs7DaysAgo;
      default:
        return this.kpis.occupancyPickupVsYesterday;
    }
  }

  get occupancyChangeValueFormatted(): string {
    const changeValue = this.occupancyChangeValue;
    const signDisplay = changeValue === 0 ? 'never' : 'always';

    const formattedValue = this.intl.formatNumber(changeValue, {
      style: 'decimal',
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
      signDisplay,
    });

    return formattedValue.concat('pt');
  }

  get occupancyPaceVsStly(): number {
    switch (this.args.smartInsightsKpiComparisonPeriod) {
      case SMART_INSIGHTS_KPI_COMPARISON_PERIODS.THREE_DAYS_AGO:
        return this.kpis.occupancyStlyPickupVs3DaysAgo;
      case SMART_INSIGHTS_KPI_COMPARISON_PERIODS.SEVEN_DAYS_AGO:
        return this.kpis.occupancyStlyPickupVs7DaysAgo;
      default:
        return this.kpis.occupancyStlyPickupVsYesterday;
    }
  }

  get occupancyPaceVsStlyFormatted(): string {
    const paceValue = this.occupancyPaceVsStly;
    const signDisplay = paceValue === 0 ? 'never' : 'always';

    const formattedValue = this.intl.formatNumber(paceValue, {
      style: 'decimal',
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
      signDisplay,
    });

    return formattedValue.concat('pt');
  }

  get occupancyValueFormatted(): string {
    const occupancyValueToFormat = this.kpis.occupancy / 100;

    return this.intl.formatNumber(occupancyValueToFormat, {
      style: 'percent',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
      signDisplay: 'never',
    });
  }

  get otbLinkText(): string {
    return this.intl.t('smart_insights.detail_view.kpi_source_link', {
      productName: this.otbSectionSource?.productName ?? '',
    });
  }

  @cached
  get otbSectionSource(): SmartInsightDayInsightEntrySource | null {
    const sources = [
      this.buildSpiderSource(),
      this.buildOverviewSource(OD_SELECTED_TILES_IDS.occupancy),
      this.buildBookingsSource(),
    ];

    return sources.find((s) => s.hasAccess) ?? null;
  }

  private get rateiRedirectId(): string {
    return (
      this.args.smartInsightDay.meta?.rateiRedirectId ?? this.subscriptionId
    );
  }

  get roomNightsChangeValue(): number {
    switch (this.args.smartInsightsKpiComparisonPeriod) {
      case SMART_INSIGHTS_KPI_COMPARISON_PERIODS.THREE_DAYS_AGO:
        return this.kpis.roomNightsPickupVs3DaysAgo;
      case SMART_INSIGHTS_KPI_COMPARISON_PERIODS.SEVEN_DAYS_AGO:
        return this.kpis.roomNightsPickupVs7DaysAgo;
      default:
        return this.kpis.roomNightsPickupVsYesterday;
    }
  }

  get roomNightsChangeValueFormatted(): string {
    const changeValue = this.roomNightsChangeValue;
    const signDisplay = changeValue === 0 ? 'never' : 'always';

    return this.intl.formatNumber(changeValue, {
      style: 'decimal',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
      signDisplay,
    });
  }

  get roomNightsPaceVsStly(): number {
    switch (this.args.smartInsightsKpiComparisonPeriod) {
      case SMART_INSIGHTS_KPI_COMPARISON_PERIODS.THREE_DAYS_AGO:
        return this.kpis.roomNightsStlyPickupVs3DaysAgo;
      case SMART_INSIGHTS_KPI_COMPARISON_PERIODS.SEVEN_DAYS_AGO:
        return this.kpis.roomNightsStlyPickupVs7DaysAgo;
      default:
        return this.kpis.roomNightsStlyPickupVsYesterday;
    }
  }

  get roomNightsPaceVsStlyFormatted(): string {
    const paceValue = this.roomNightsPaceVsStly;
    const signDisplay = paceValue === 0 ? 'never' : 'always';

    return this.intl.formatNumber(paceValue, {
      style: 'decimal',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
      signDisplay,
    });
  }

  get roomNightsValueFormatted(): string {
    return this.intl.formatNumber(this.kpis.roomNights, {
      style: 'decimal',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
  }

  get selectedComparisonPeriodOption(): ComparisonPeriodOption | undefined {
    return this.comparisonPeriodOptions.find(
      (option) => option.value === this.args.smartInsightsKpiComparisonPeriod,
    );
  }

  private get stayDate(): string {
    return this.args.smartInsightDay.stayDate;
  }

  private get subscriptionId(): string {
    return this.args.smartInsightDay.generationId?.split(',')[0] ?? '';
  }

  private buildBookingsSource(): SmartInsightDayInsightEntrySource {
    const queryParams = {
      reference: 'SWLY',
      fromDate: this.stayDate,
      toDate: this.stayDate,
      smartInsightsDay: this.stayDate,
    };

    const routeName = 'hotel.revenue-insight';
    const href = this.router.urlFor(routeName, this.biRedirectId, {
      queryParams,
    });

    return {
      productName: this.intl.t('menu.bookings'),
      href,
      navigateToSource: (targetBlank = false): void => {
        if (targetBlank) {
          openUrl(href);
        } else {
          this.router.transitionTo(routeName, this.biRedirectId, {
            queryParams,
          });
        }
      },
      hasAccess: this.hasFeature('app.revenue_insight'),
    };
  }

  private buildMarketInsightSource(): SmartInsightDayInsightEntrySource {
    const activeFilters = {
      [ACTIVE_FILTERS.DAY]: this.stayDate,
    };
    const queryParams = {
      activeFilters: stringifyObject(activeFilters),
      highlight: DETAIL_PANE_DETAIL_SECTION_CARD_IDS.DEMAND_INDICATORS,
      smartInsightsDay: this.stayDate,
    };

    const routeName = 'hotel.market-insight';
    const href = this.router.urlFor(routeName, this.biRedirectId, {
      queryParams,
    });

    return {
      productName: this.intl.t('menu.market'),
      href,
      navigateToSource: (targetBlank = false): void => {
        if (targetBlank) {
          openUrl(href);
        } else {
          this.router.transitionTo(routeName, this.biRedirectId, {
            queryParams,
          });
        }
      },
      hasAccess: this.hasFeature('app.market_insight'),
    };
  }

  private buildOverviewSource(
    highlightTileId: string,
  ): SmartInsightDayInsightEntrySource {
    const fromDate = dayjs(this.stayDate).startOf('month').format('YYYY-MM-DD');
    const toDate = dayjs(this.stayDate).endOf('month').format('YYYY-MM-DD');

    const queryParams = {
      activeFilters: stringifyObject({
        [ACTIVE_FILTERS.DAY]: this.stayDate,
      }),
      fromDate,
      toDate,
      rateParam: RATE_PARAMS.LOWEST,
      smartInsightsDay: this.stayDate,
      highlight: highlightTileId,
    };

    const routeName = 'hotel.overview';
    const href = this.router.urlFor(routeName, this.biRedirectId, {
      queryParams,
    });

    return {
      productName: this.intl.t('menu.overview'),
      href,
      navigateToSource: (targetBlank = false): void => {
        this.hotelAndMarketService.setActiveCompsetById('1');

        if (targetBlank) {
          openUrl(href);
        } else {
          this.router.transitionTo(routeName, this.biRedirectId, {
            queryParams,
          });
        }
      },
      hasAccess: this.hasFeature('overview.view'),
    };
  }

  private buildRateInsightSource(): SmartInsightDayInsightEntrySource {
    const month = this.stayDate.slice(0, 7);
    const queryParams = {
      compsetId: '1',
      highlight: `smart-insight-day-${this.stayDate}`,
      month,
      rateParam: RATE_PARAMS.LOWEST,
      smartInsightsDay: this.stayDate,
      view: RATES_VIEWS.CALENDAR,
    };

    const routeName = 'hotel.rates';
    const href = this.router.urlFor(routeName, this.rateiRedirectId, {
      queryParams,
    });

    return {
      productName: this.intl.t('menu.rates'),
      href,
      navigateToSource: (targetBlank = false): void => {
        this.hotelAndMarketService.setActiveCompsetById('1');

        if (targetBlank) {
          openUrl(href);
        } else {
          this.router.transitionTo(routeName, this.rateiRedirectId, {
            queryParams,
          });
        }
      },
      hasAccess: this.hasFeature('app.rates'),
    };
  }

  private buildSpiderSource(): SmartInsightDayInsightEntrySource {
    const yesterday = dayjs().subtract(1, 'day').format('YYYY-MM-DD');
    const startDate = dayjs(this.stayDate)
      .startOf('month')
      .format('YYYY-MM-DD');
    const endDate = dayjs(this.stayDate).endOf('month').format('YYYY-MM-DD');
    const queryParams = {
      pickupFrom: yesterday,
      startDate,
      endDate,
      smartInsightsDay: this.stayDate,
    };

    const routeName = 'hotel.day-by-day.spider-dashboard';
    const href = this.router.urlFor(
      routeName,
      this.biRedirectId,
      DAY_BY_DAY_PATHS[0],
      { queryParams },
    );

    return {
      productName: this.intl.t('menu.spider.day-by-day'),
      href,
      navigateToSource: (targetBlank = false): void => {
        if (targetBlank) {
          openUrl(href);
        } else {
          this.router.transitionTo(
            routeName,
            this.biRedirectId,
            DAY_BY_DAY_PATHS[0],
            { queryParams },
          );
        }
      },
      hasAccess: this.hasFeature('app.spider'),
    };
  }

  private hasFeature(featureString: string): boolean {
    return (
      this.args.smartInsightDay.meta?.featureFlags?.includes(featureString) ??
      false
    );
  }

  @action handleSourceClick(
    source: SmartInsightDayInsightEntrySource,
    event: MouseEvent,
  ): void {
    event.preventDefault();
    source.navigateToSource(event.metaKey || event.ctrlKey);
  }

  @action onChangeComparisonPeriod(
    selectedOption: ComparisonPeriodOption,
  ): void {
    this.args.setSmartInsightsKpiComparisonPeriod(selectedOption.value);
  }

  <template>
    <div
      class="ic-product-tour-57546189-smart-insights-kpis flex flex-col gap-200"
    >
      <div class="flex flex-row items-center gap-200">
        <h4 class="text-400--bold">
          {{t "smart_insights.detail_view.kpis"}}
        </h4>

        {{#if this.isComparisonPeriodDropdownVisible}}
          <PrismSelectInput
            @isSmall={{true}}
            @onChange={{this.onChangeComparisonPeriod}}
            @options={{this.comparisonPeriodOptions}}
            @value={{this.selectedComparisonPeriodOption}}
          />
        {{/if}}
      </div>

      <GeneralSmartInsightsDetailViewSmartInsightCard class="gap-400 p-400">
        {{#if this.isOtbSectionVisible}}
          {{#if this.isOccupancyBlockVisible}}
            <GeneralSmartInsightsDetailViewSmartInsightKpiBlock
              @changeValue={{this.occupancyChangeValue}}
              @changeValueFormatted={{this.occupancyChangeValueFormatted}}
              @label={{t "general.occupancy"}}
              @paceValue={{this.occupancyPaceVsStly}}
              @paceValueFormatted={{this.occupancyPaceVsStlyFormatted}}
              @value={{this.occupancyValueFormatted}}
            />
          {{/if}}

          {{#if this.isRoomNightsBlockVisible}}
            <GeneralSmartInsightsDetailViewSmartInsightKpiBlock
              @changeValue={{this.roomNightsChangeValue}}
              @changeValueFormatted={{this.roomNightsChangeValueFormatted}}
              @label={{t "general.room_nights"}}
              @paceValue={{this.roomNightsPaceVsStly}}
              @paceValueFormatted={{this.roomNightsPaceVsStlyFormatted}}
              @value={{this.roomNightsValueFormatted}}
            />
          {{/if}}

          {{#if this.isAdrBlockVisible}}
            <GeneralSmartInsightsDetailViewSmartInsightKpiBlock
              @changeValue={{this.adrChangeValue}}
              @changeValueFormatted={{this.adrChangeValueFormatted}}
              @label={{t "revenue_insight.adr"}}
              @value={{this.adrValueFormatted}}
            />
          {{/if}}

          {{#if this.otbSectionSource}}
            <a
              class="text-200--regular text-interactive-default"
              data-test-id="smart-insight-kpi-otb-source-link"
              href={{this.otbSectionSource.href}}
              {{on "click" (fn this.handleSourceClick this.otbSectionSource)}}
            >
              {{this.otbLinkText}}
            </a>
          {{/if}}
        {{/if}}

        {{#if this.isDividerVisible}}
          <div class={{styles.kpi-divider}} />
        {{/if}}

        {{#if this.isMarketDemandBlockVisible}}
          <GeneralSmartInsightsDetailViewSmartInsightKpiBlock
            @demandLevel={{this.demandLevel}}
            @isChangeValueHidden={{true}}
            @label={{t "demand.market_demand"}}
            @value={{this.demandValueFormatted}}
          />

          {{#if this.demandSectionSource}}
            <a
              class="text-200--regular text-interactive-default"
              data-test-id="smart-insight-kpi-demand-source-link"
              href={{this.demandSectionSource.href}}
              {{on
                "click"
                (fn this.handleSourceClick this.demandSectionSource)
              }}
            >
              {{this.demandLinkText}}
            </a>
          {{/if}}
        {{/if}}
      </GeneralSmartInsightsDetailViewSmartInsightCard>
    </div>
  </template>
}
